import {
  Api,
  AuthenticationOptions,
  getDefaultClient,
  types as api,
  PartnerApi,
  VendorApi
} from '@mesa-labs/mesa-api';
import {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import cognitoProvider from '../cognito';

type ApiCtor<T extends Api> = new (
  baseURL: string,
  options?: AuthenticationOptions,
  client?: AxiosInstance
) => T;

const getHeaders = () => ({
  mode: 'cors',
  cache: 'no-cache',
  credentials: 'same-origin',
  Accept: 'application/json',
  'Content-Type': 'application/json',
});

let inFlightRefresh: ReturnType<typeof cognitoProvider.refreshCurrentSession> | undefined;

export const ensureIdToken = async (): Promise<string | undefined> => {
  let session = cognitoProvider.getCurrentSession();
  if (!session?.isValid()) {
    if (!inFlightRefresh) {
      inFlightRefresh = cognitoProvider.refreshCurrentSession().finally(() => {
        inFlightRefresh = undefined;
      });
    }
    session = await inFlightRefresh;
  }
  return session?.isValid() ? session?.getIdToken()?.getJwtToken() : undefined;
};

export const createApiClient = <T extends Api>(
  baseURL: string,
  Ctor: ApiCtor<T>
): T => {
  const client = getDefaultClient(baseURL, getHeaders());

  client.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
    const idToken = await ensureIdToken();
    if (idToken) {
      config.headers.set('Authorization', `Bearer ${idToken}`);
    }
    return config;
  });

  client.interceptors.response.use(
    (response: AxiosResponse) => ({
      ...response,
      data: response.status === 404 ? null : response.data,
    }),
    async (err?: AxiosError) => {
      const statusCode = err?.response?.status;
      if (statusCode === 401 || (statusCode === 403 && !err?.config?.headers?.Authorization)) {
        await cognitoProvider.signOut();
      }
      throw api.ApiError.fromAxiosError(err);
    }
  );

  return new Ctor(baseURL, undefined, client);
};

export const createVendorPartnerApiClient = () =>
  createApiClient(CONFIG.api.vendorUrl, PartnerApi);

export const createInvoicePartnerApiClient = () =>
  createApiClient(CONFIG.api.invoiceUrl, PartnerApi);

export const createVendorApiClient = () =>
  createApiClient(CONFIG.api.vendorUrl, VendorApi);